import {
  GatewayContact,
  GatewayDeclarationActivite,
  GatewayEntreprise,
} from "@conformite/gateway";
import {
  isIas,
  isIobsp,
  Button,
  displayErrorToast,
  displaySucessToast,
} from "@endya-conformite/ui-kit";
import { GatewayDeclarationActiviteApi } from "@src/api/conformite/declarationActivite.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { useIsAllowed } from "@src/session/useIsAllowed";
import { useEntreprises } from "@src/store/store.entreprise";
import { useMe } from "@src/store/store.me";

type CopyDeclarationActiviteButtonProps = {
  declarationActivite:
    | GatewayDeclarationActivite.DeclarationActiviteIobsp
    | GatewayDeclarationActivite.DeclarationActiviteIas
    | null;
  isConforme: boolean;
  categorie: "ias" | "iobsp";
};

export function CopyDeclarationActiviteButton({
  declarationActivite,
  isConforme,
  categorie,
}: CopyDeclarationActiviteButtonProps) {
  const { entreprisesBySiren, updateAvancement } = useEntreprises();
  const { me } = useMe();
  const typeAdhesion = me?.entreprisePrincipale.typeAdhesion;
  const { hasAllowedRole } = useIsAllowed();

  const handleCopy = async () => {
    const sirens = Object.keys(entreprisesBySiren ?? {}).filter(
      (s) => s !== declarationActivite?.siren
    );
    if (!declarationActivite?.siren || !declarationActivite?.millesime) return;

    try {
      const filteredSirens = sirens.filter((siren) => {
        const entreprise = entreprisesBySiren && entreprisesBySiren[siren];
        const categories = entreprise?.categoriesActivites ?? [];
        const categoriesAccessoires =
          entreprise?.categoriesActivitesAccessoires ?? [];

        return (
          siren !== declarationActivite?.siren &&
          (categorie === "ias"
            ? isIas([...categories, ...categoriesAccessoires])
            : isIobsp([...categories, ...categoriesAccessoires]))
        );
      });

      const avancements = await GatewayDeclarationActiviteApi.copy(
        {
          siren: declarationActivite.siren,
          millesime: declarationActivite.millesime,
          categorie,
        },
        { sirens: filteredSirens }
      );

      if (avancements) {
        avancements.forEach((avancement) => updateAvancement(avancement));
      }

      displaySucessToast(
        `Déclaration Activité ${categorie.toUpperCase()} copiée avec succès`
      );
    } catch (err) {
      handleGatewayError({
        onUnhandled: () => {
          displayErrorToast(
            "Une erreur est survenue lors de la récupération du formulaire"
          );
        },
      })(err);
    }
  };

  if (
    isConforme &&
    hasAllowedRole(GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR) &&
    (typeAdhesion === GatewayEntreprise.TypeAdhesion.GROUPE ||
      typeAdhesion === GatewayEntreprise.TypeAdhesion.GROUPEMENT_MANDATAIRES)
  )
    return (
      <Button
        variant="outline-accent"
        onClick={() => {
          handleCopy().catch(console.error);
        }}
      >
        Copier l&apos;axe pour les autres sociétés
      </Button>
    );

  return null;
}
